<template>
  <v-container>
    <v-row>
      <v-col
        cols="12"
        :xl="
          person.certifications ||
          person.education ||
          person.effectiveness_keys ||
          person.external_links ||
          person.fun_fact ||
          person.intro_video ||
          person.specialties
            ? 8
            : 12
        "
      >
        <PersonCardWithDescription :value="person" image-key="list_image">
          <template #image-content>
            <ShareLinkButton class="ma-1" variant="flat" />
          </template>

          <template #footer>
            <v-row v-if="randomReview">
              <v-col>
                <v-card class="mb-6" color="red-darken-2 " tile variant="flat">
                  <v-card-text>
                    <div class="mb-3">
                      <ReviewStars :value="randomReview.stars" class="mr-2" />

                      <v-chip color="primary" variant="flat">
                        <ReviewSource :value="randomReview.source" />
                        <ReviewName prepend-text="–" :value="randomReview" />
                      </v-chip>
                    </div>
                    <p class="text-body-0">{{ randomReview.long_text }}</p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </PersonCardWithDescription>
      </v-col>

      <v-col
        v-if="
          person.certifications ||
          person.education ||
          person.effectiveness_keys ||
          person.external_links ||
          person.fun_fact ||
          person.intro_video ||
          person.specialties
        "
        cols="12"
        xl="4"
      >
        <v-row v-if="person.intro_video">
          <v-col cols="12">
            <v-card color="primary mb-6" tile variant="flat">
              <v-card-text class="py-6">
                <h5 class="text-h5 text-center mb-2">Overview</h5>
                <p class="text-body-1 font-weight-bold text-center">
                  About {{ person.name }}
                </p>
              </v-card-text>

              <VideoCover
                :aspect-ratio="videoAspectRatio"
                :gradient="TRAINER_VIDEO_GRADIENT"
                :value="person.intro_video"
                content-class="d-flex align-center justify-center"
              >
                <PlayVideo :video="person.intro_video" />
              </VideoCover>
            </v-card>
          </v-col>
        </v-row>

        <v-row
          v-if="
            person.certifications ||
            person.education ||
            person.effectiveness_keys ||
            person.external_links ||
            person.fun_fact ||
            person.specialties
          "
          dense
        >
          <v-col
            v-if="
              Array.isArray(person.effectiveness_keys) &&
              person.effectiveness_keys.length
            "
            cols="12"
            lg="6"
            xl="12"
          >
            <v-card tile variant="flat">
              <v-card-title class="text-h5">Keys to Effectiveness</v-card-title>
              <v-card-text>
                <ul class="text-body-1 ml-6">
                  <li
                    v-for="(item, i) in person.effectiveness_keys"
                    :key="i"
                    class="mb-2"
                  >
                    {{ item.text }}
                  </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            v-if="
              Array.isArray(person.specialties) && person.specialties.length
            "
            cols="12"
            lg="6"
            xl="12"
          >
            <v-card tile variant="flat">
              <v-card-title class="text-h5">Specialty Industries</v-card-title>
              <v-card-text>
                <ul class="text-body-1 ml-6">
                  <li
                    v-for="(item, i) in person.specialties"
                    :key="i"
                    class="mb-2"
                  >
                    {{ item.text }}
                  </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            v-if="
              Array.isArray(person.certifications) &&
              person.certifications.length
            "
            cols="12"
            lg="6"
            xl="12"
          >
            <v-card tile variant="flat">
              <v-card-title class="text-h5">Certifications</v-card-title>
              <v-card-text>
                <ul class="text-body-1 ml-6">
                  <li
                    v-for="(item, i) in person.certifications"
                    :key="i"
                    class="mb-2"
                  >
                    {{ item.text }}
                  </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            v-if="Array.isArray(person.education) && person.education.length"
            cols="12"
            lg="6"
            xl="12"
          >
            <v-card tile variant="flat">
              <v-card-title class="text-h5">Education</v-card-title>
              <v-card-text>
                <ul class="text-body-1 ml-6">
                  <li
                    v-for="(item, i) in person.education"
                    :key="i"
                    class="mb-2"
                  >
                    {{ item.text }}
                  </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col v-if="person.fun_fact" cols="12" lg="6" xl="12">
            <v-card tile variant="flat">
              <v-card-title class="text-h5">Fun Fact</v-card-title>
              <v-card-text class="text-body-1">
                {{ person.fun_fact }}
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            v-if="
              Array.isArray(person.external_links) &&
              person.external_links.length
            "
            cols="12"
            lg="12"
            xl="12"
          >
            <v-card tile variant="flat">
              <v-card-title class="text-h5">Additional Resources</v-card-title>
              <v-list density="compact" lines="one" slim>
                <v-list-item
                  v-for="(link, i) in person.external_links"
                  :key="i"
                  :href="link.url"
                  :prepend-icon="mdiOpenInNew"
                  :title="link.title"
                  target="_blank"
                />
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import type { components } from '#root/types/directus'
import { computed } from 'vue'
import { TRAINER_VIDEO_GRADIENT } from '#root/lib/consts'
import { mdiOpenInNew } from '@mdi/js'

const props = defineProps<{
  person: components['schemas']['ItemsPersons']
}>()

const reviews = computed(() => {
  return (
    props.person.reviews
      ?.filter(
        (
          review
        ): review is {
          reviews_id: components['schemas']['ItemsReviews']
        } =>
          typeof review === 'object' &&
          review !== null &&
          'reviews_id' in review &&
          review.reviews_id !== null &&
          typeof review.reviews_id === 'object'
      )
      .map(review => review.reviews_id) ?? []
  )
})

const randomReview = computed(() => {
  return reviews.value[Math.floor(Math.random() * reviews.value.length)]
})

const videoAspectRatio = 16 / 9
</script>
